import { PointCloudStyle } from '@skand/three-js-renderer';
import { Color } from 'three';
import { toCartesian, toCartographic } from '../../../Utils';
import { Model3D } from './Model3D';
/**
 * Colors for the elevation gradient, sorted from low to high altitude
 */
const ELEVATION_GRADIENT_COLORS = [
    new Color(0, 0.1, 1),
    new Color(0, 0.7, 1),
    new Color(0, 1, 0.98),
    new Color(0, 1, 0.42),
    new Color(0.02, 1, 0.01),
    new Color(0.65, 1, 0),
    new Color(0.98, 1, 0),
    new Color(1, 0.67, 0),
    new Color(1, 0.09, 0),
];
/**
 * Tileset model.
 */
export class Tileset extends Model3D {
    /**
     * Create a new tileset model.
     *
     * @param object
     */
    constructor(object) {
        super(object);
        Object.defineProperty(this, "tileset", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "defaultStyle", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "style", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.tileset = object;
        const height = toCartographic(this.getPosition()).height;
        this.defaultStyle = {
            type: 'rgb',
            intensityColors: [new Color(0, 0, 0), new Color(1, 1, 1)],
            classificationColors: {
                0: { base: new Color(1, 1, 1), show: true },
                1: { base: new Color(1, 1, 1), show: true },
                2: { base: new Color(0.5, 0.25, 0), show: true },
                3: { base: new Color(0.68, 1, 0.184), show: true },
                4: { base: new Color(0, 1, 0), show: true },
                5: { base: new Color(0, 0.5, 0), show: true },
                6: { base: new Color(0.7, 0.7, 0.7), show: true },
                7: { base: new Color(1, 0.65, 0), show: true },
                8: { base: new Color(1, 1, 1), show: true },
                9: { base: new Color(0, 0, 1), show: true },
                10: { base: new Color(0.7, 0.7, 0.7), show: true },
                11: { base: new Color(0, 0.5, 0), show: true },
                12: { base: new Color(1, 1, 1), show: true },
                13: { base: new Color(0, 0.5, 0), show: true },
                14: { base: new Color(0, 0.5, 0), show: true },
                15: { base: new Color(0, 0.5, 0), show: true },
                16: { base: new Color(0, 0.5, 0), show: true },
                17: { base: new Color(0, 0.5, 0), show: true },
                18: { base: new Color(0, 0.5, 0), show: true },
            },
            contrast: 1.0,
            brightness: 0.0,
            gamma: 1.0,
            alpha: 1.0,
            pointSize: 1.0,
            minHeight: height - this.getBoundingRadius(),
            maxHeight: height + this.getBoundingRadius(),
        };
        this.style = {
            ...this.defaultStyle,
        };
        this.applyRendererStyle(this.style);
    }
    /**
     * Get the list of classification ids available to this tileset.
     *
     * If the tileset is not a point cloud, this will return an empty list.
     *
     * @returns
     */
    getClassificationIds() {
        return this.tileset.pointChannels.Classification.integersPresent ?? [];
    }
    /**
     * Get the list of point channels availalbe in this tileset.
     *
     * @returns
     */
    getPointChannels() {
        return Object.keys(this.tileset.pointChannels);
    }
    /**
     * Get the current style.
     *
     * @returns
     */
    getStyle() {
        return structuredClone(this.style);
    }
    /**
     * Get the default style.
     *
     * @returns
     */
    getDefaultStyle() {
        return structuredClone(this.defaultStyle);
    }
    /**
     * Get the maximum screen space error of the tileset.
     *
     * @returns
     */
    getMSSE() {
        return this.tileset.maximumScreenSpaceError;
    }
    /**
     * Get the tile budget.
     *
     * @returns
     */
    getTileBudget() {
        return this.tileset.tileBudget;
    }
    /**
     * Get the attributions of the tileset.
     *
     * @returns
     */
    getAttributions() {
        return this.tileset.attributions;
    }
    /**
     * Set the style of the tileset.
     *
     * @param style
     */
    setStyle(style) {
        if (JSON.stringify(style) !== JSON.stringify(this.style)) {
            this.applyRendererStyle(style);
            this.style = structuredClone(style);
        }
    }
    /**
     * Set the maximum screen space error of the tileset.
     *
     * @param maximumScreenSpaceError
     */
    setMSSE(maximumScreenSpaceError) {
        this.tileset.maximumScreenSpaceError = maximumScreenSpaceError;
    }
    /**
     * Set the tile budget to determine how many tiles can be kept in cache at any time.
     *
     * @param tileBudget
     */
    setTileBudget(tileBudget) {
        this.tileset.tileBudget = tileBudget;
    }
    /**
     * Convert a tileset style to the format expected by the renderer.
     *
     * @param style
     */
    applyRendererStyle(style) {
        // Grab the min and max position of the tileset relative to surface normal
        const cartographic = toCartographic(this.getPosition());
        cartographic.height = style.minHeight;
        const min = toCartesian(cartographic);
        cartographic.height = style.maxHeight;
        const max = toCartesian(cartographic);
        const styleDesc = {
            pointSize: style.pointSize,
            classificationColors: style.classificationColors,
            intensityColors: style.intensityColors,
            gradientColors: ELEVATION_GRADIENT_COLORS,
            gradient: { min, max },
            brightness: style.brightness,
            contrast: style.contrast,
            gamma: style.gamma,
            opacity: style.alpha,
        };
        switch (style.type) {
            case 'rgb':
                styleDesc.pointStyle = PointCloudStyle.None;
                break;
            case 'intensity':
                styleDesc.pointStyle = PointCloudStyle.IntensityColoring;
                break;
            case 'classification':
                styleDesc.pointStyle = PointCloudStyle.ClassificationColoring;
                break;
            case 'elevation':
                styleDesc.pointStyle = PointCloudStyle.GradientColoring;
                break;
            default:
                break;
        }
        this.tileset.applyStyle(styleDesc);
    }
}
